import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
    SignOut,
    List,
    ChartBar,
    ClockClockwise,
    UsersThree,
    UserCheck,
    Boat,
    Gear,
    CalendarBlank,
    Info,
    EnvelopeSimple,
    X,
    SpeakerHigh,
    SpeakerSlash,
    Lock,
    Train,
    Stack,
} from "@phosphor-icons/react";
import GrindrodLogo from "./GrindrodLogo";
import VoiceAssistant from "./VoiceAssistant";
import ProactiveAlerts from "./ProactiveAlerts";
import MillwrightAlerts from "./MillwrightAlerts";
import ShortfallBell from "./ShortfallBell";
import BreakdownBanner from "./BreakdownBanner";
import ChangePasswordModal from "./ChangePasswordModal";
import DataVaultModal from "./DataVaultModal";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "./ui/dropdown-menu";

export default function Header() {
    const { user, logout } = useAuth();
    const [showVault, setShowVault] = React.useState(false);
    const nav = useNavigate();
    const [showAbout, setShowAbout] = useState(false);
    const [showChangePw, setShowChangePw] = useState(false);
    const [muted, setMuted] = useState(() => {
        try { return sessionStorage.getItem("grindrod_alerts_muted") === "1"; } catch { return false; }
    });
    const toggleMute = () => {
        setMuted((prev) => {
            const next = !prev;
            try { sessionStorage.setItem("grindrod_alerts_muted", next ? "1" : "0"); } catch { /* ignore */ }
            if (next && window.speechSynthesis) window.speechSynthesis.cancel();
            return next;
        });
    };
    const canMute = user && (user.role === "admin" || user.role === "manager");
    const canExtract = user && (user.role === "admin" || user.role === "manager");

    const item = (to, label, Icon, testid) => (
        <DropdownMenuItem
            key={to}
            onClick={() => {
                // Explicit navigation with no lingering search params — from
                // rail dashboards the `?branch=xxx` query would otherwise
                // stick and land users on the wrong Portal tab.
                nav({ pathname: to, search: "" });
            }}
            className="cursor-pointer gap-2.5 py-2.5"
            data-testid={testid}
        >
            <Icon size={16} />
            <span>{label}</span>
        </DropdownMenuItem>
    );

    return (
        <>
        <header className="g-header sticky top-0 z-40">
            <div className="max-w-[1600px] mx-auto px-5 py-3 flex items-center gap-6">
                <Link to="/portal" className="flex items-center gap-3 min-w-0" data-testid="brand-logo">
                    {/* Compact logo on mobile, full branding on md+ */}
                    <span className="md:hidden">
                        <GrindrodLogo variant="dark" size="sm" subline={null} slogan={null} />
                    </span>
                    <span className="hidden md:inline-flex">
                        <GrindrodLogo variant="dark" size="md" subtitle="RICHARDS BAY DIVISION" />
                    </span>
                </Link>

                <div className="ml-auto flex items-center gap-3">
                    <span className="live-dot" aria-label="live"></span>
                    <span className="uppercase-label text-white text-[10px]">Live</span>

                    {user && (
                        <div className="flex items-center gap-3 ml-3 pl-3 border-l border-white/20">
                            <MillwrightAlerts />
                            <ShortfallBell />
                            {canExtract && (
                                <button
                                    onClick={() => setShowVault(true)}
                                    className="inline-flex items-center justify-center w-10 h-10 rounded-sm bg-white/10 hover:bg-white/20 text-white transition-colors"
                                    title="Data Vault · extract data to ZIP, folder or SFTP"
                                    data-testid="header-data-vault-btn"
                                    aria-label="Open Data Vault"
                                >
                                    📦
                                </button>
                            )}
                            {canMute && (
                                <button
                                    onClick={toggleMute}
                                    className={`inline-flex items-center justify-center w-10 h-10 rounded-sm transition-colors ${
                                        muted ? "bg-[var(--grindrod-red)] text-white" : "bg-white/10 hover:bg-white/20 text-white"
                                    }`}
                                    title={muted ? "Voice alerts muted — tap to unmute" : "Mute voice alerts"}
                                    data-testid="alerts-mute-btn"
                                    aria-label={muted ? "Unmute alerts" : "Mute alerts"}
                                >
                                    {muted ? <SpeakerSlash size={18} weight="fill" /> : <SpeakerHigh size={18} weight="fill" />}
                                </button>
                            )}
                            <div className="text-right leading-tight hidden sm:block">
                                <div className="text-sm font-semibold text-white" data-testid="header-user-name">
                                    {user.name}
                                </div>
                                <div className="uppercase-label text-[9px] text-slate-300" data-testid="header-user-role">
                                    {user.role} · {user.company_number}
                                </div>
                            </div>

                            <DropdownMenu>
                                <DropdownMenuTrigger
                                    asChild
                                    data-testid="header-menu-btn"
                                >
                                    <button
                                        className="inline-flex items-center justify-center w-10 h-10 rounded-sm bg-white/10 hover:bg-white/20 text-white transition-colors"
                                        title="Menu"
                                    >
                                        <List size={20} weight="bold" />
                                    </button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-64 rounded-sm">
                                    <DropdownMenuLabel className="uppercase-label text-[10px]">
                                        {user.name}
                                    </DropdownMenuLabel>
                                    <DropdownMenuSeparator />
                                    {item("/portal", "Portal Overview", Gear, "menu-portal")}
                                    {item("/dashboard", "RB Vessel", Boat, "menu-dashboard")}
                                    {item("/rail-operations", "Rail Operations", Train, "menu-rail-ops")}
                                    {item("/rail-consignments", "Rail Consignments", Train, "menu-rail-consignments")}
                                    {item("/portal?branch=navitrade-stockpile", "Navitrade Stockpile", Stack, "menu-navitrade-stockpile")}
                                    {item("/lineup", "Vessel Line-Up", CalendarBlank, "menu-lineup")}
                                    {item("/history", "Completed Vessels", ClockClockwise, "menu-history")}
                                    {user.role !== "supervisor" && (
                                        <>
                                            <DropdownMenuSeparator />
                                            <DropdownMenuLabel className="uppercase-label text-[10px]">
                                                Management
                                            </DropdownMenuLabel>
                                            {item("/analytics", "Analytics", ChartBar, "menu-analytics")}
                                            {item("/members", "Members", UsersThree, "menu-members")}
                                            {item("/approvals", "Registration Approvals", UserCheck, "menu-approvals")}
                                        </>
                                    )}
                                    {user.role === "supervisor" && (
                                        <>
                                            <DropdownMenuSeparator />
                                            {item("/lineup", "Vessel Line-up", Boat, "menu-lineup")}
                                        </>
                                    )}
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                        onClick={() => setShowChangePw(true)}
                                        className="cursor-pointer gap-2.5 py-2.5"
                                        data-testid="menu-change-password"
                                    >
                                        <Lock size={16} />
                                        <span>Change password</span>
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                        onClick={() => setShowAbout(true)}
                                        className="cursor-pointer gap-2.5 py-2.5"
                                        data-testid="menu-about"
                                    >
                                        <Info size={16} />
                                        <span>App Info</span>
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                        onClick={async () => {
                                            await logout();
                                            nav("/login");
                                        }}
                                        className="cursor-pointer gap-2.5 py-2.5 text-[var(--grindrod-red)]"
                                        data-testid="menu-logout"
                                    >
                                        <SignOut size={16} />
                                        <span>Sign out</span>
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </div>
                    )}
                </div>
            </div>
            {showAbout && <AboutModal onClose={() => setShowAbout(false)} />}
            {showChangePw && <ChangePasswordModal onClose={() => setShowChangePw(false)} />}
            {showVault && canExtract && <DataVaultModal open={showVault} onClose={() => setShowVault(false)} />}
            {user && <VoiceAssistant />}
            {user && <ProactiveAlerts />}
        </header>
        {user && <BreakdownBanner />}
        </>
    );
}

function AboutModal({ onClose }) {
    return (
        <div
            className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
            onClick={onClose}
            data-testid="about-modal"
        >
            <div
                className="g-card max-w-md w-full overflow-hidden"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="bg-[var(--grindrod-navy)] text-white px-5 py-4 flex items-center gap-3 border-b-[3px] border-[var(--grindrod-red)]">
                    <Info size={20} weight="duotone" />
                    <div className="flex-1">
                        <div className="uppercase-label text-[9px] text-slate-300">About</div>
                        <div className="font-display font-bold">Grindrod Vessel Tracker</div>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-white/80 hover:text-white"
                        data-testid="about-close"
                        aria-label="Close"
                    >
                        <X size={18} />
                    </button>
                </div>
                <div className="p-6 space-y-5 text-sm text-slate-700">
                    <div>
                        <div className="uppercase-label text-[10px] text-[var(--grindrod-red)] mb-1">Live Vessel Loading Tracker</div>
                        <div className="font-display font-bold text-[var(--grindrod-navy)] text-base">
                            Grindrod Terminals · Richards Bay Division
                        </div>
                        <div className="text-slate-500 text-xs italic mt-0.5">"The Grindrod Way"</div>
                    </div>

                    <div className="border-t border-slate-100 pt-4">
                        <div className="uppercase-label text-[10px] text-slate-500 mb-2">Built by</div>
                        <div className="font-display font-bold text-[var(--grindrod-navy)] text-base">Hannes Bester</div>
                        <a
                            href="mailto:bester881219@gmail.com"
                            className="inline-flex items-center gap-1.5 text-[var(--grindrod-red)] text-sm mt-1 hover:underline"
                            data-testid="about-email-link"
                        >
                            <EnvelopeSimple size={14} weight="fill" />
                            bester881219@gmail.com
                        </a>
                    </div>

                    <div className="border-t border-slate-100 pt-4">
                        <div className="uppercase-label text-[10px] text-slate-500 mb-1.5">Powered by</div>
                        <div className="text-sm">
                            Built with <a
                                href="https://emergent.sh"
                                target="_blank"
                                rel="noreferrer"
                                className="font-semibold text-[var(--grindrod-navy)] hover:underline"
                            >Emergent</a> — full-stack agentic engineering platform.
                        </div>
                    </div>

                    <div className="border-t border-slate-100 pt-4 text-xs text-slate-500">
                        Submit feedback, report bugs, or request features by emailing the address above.
                    </div>
                </div>
            </div>
        </div>
    );
}
